import { useTranslation } from 'react-i18next'
import { addDays, format, getDate, isSameMonth, isToday, startOfWeek } from 'date-fns'
import { dateLocale } from '@/lib/dateLocale'
import type { Appointment, Client } from '@/lib/api'
import { MonthAppointmentEvent } from './AppointmentEvent'
import { eventColor } from './constants'

export interface MonthViewProps {
  /** 当前月锚点日期 */
  date: Date
  /** 月视图 42 天网格 */
  days: Date[]
  /** 按天聚合的预约映射（key: yyyy-MM-dd） */
  byDay: Map<string, Appointment[]>
  /** 学员映射 */
  clientsMap: Map<number, Client>
  /** 每天最多显示事件数 */
  maxEventsPerDay?: number
  onEventClick: (appt: Appointment) => void
  onDayClick: (date: Date) => void
  onMoreClick: (date: Date) => void
}

const WEEK_START = startOfWeek(new Date(2026, 0, 5), { weekStartsOn: 1 })

export function MonthView({
  date,
  days,
  byDay,
  clientsMap,
  maxEventsPerDay = 2,
  onEventClick,
  onDayClick,
  onMoreClick,
}: MonthViewProps) {
  const { t, i18n } = useTranslation()
  // 星期表头跟随界面语言（OPT-005）：EEEEEE 为 date-fns 单字缩写（一/月/월/Mo）
  const locale = dateLocale(i18n.language)
  const weekdays = Array.from({ length: 7 }, (_, i) => format(addDays(WEEK_START, i), 'EEEEEE', { locale }))

  return (
    <div className="flex flex-col h-full" data-testid="month-view">
      <div className="grid grid-cols-7 border-b border-slate-200">
        {weekdays.map((d) => (
          <div key={d} className="text-center text-xs font-medium text-slate-500 py-2">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 flex-1">
        {days.map((day) => {
          const key = format(day, 'yyyy-MM-dd')
          const list = byDay.get(key) || []
          const visible = list.slice(0, maxEventsPerDay)
          const overflow = list.length - visible.length
          const inMonth = isSameMonth(day, date)
          const todayFlag = isToday(day)

          return (
            <div
              key={key}
              data-date={key}
              data-testid={`month-day-${key}`}
              className={`
                min-h-[80px] border-b border-r border-slate-100 p-1 cursor-pointer
                hover:bg-slate-50 transition-colors
                ${!inMonth ? 'bg-slate-50/50 text-slate-400' : ''}
              `}
              onClick={() => onDayClick(day)}
            >
              <div className={`text-xs mb-1 w-5 h-5 flex items-center justify-center rounded-full ${todayFlag ? 'bg-primary text-primary-foreground font-bold' : ''}`}>
                {getDate(day)}
              </div>
              <div className="space-y-0.5">
                {visible.map((appt) => (
                  <MonthAppointmentEvent
                    key={appt.id}
                    appt={appt}
                    client={appt.clientId ? clientsMap.get(appt.clientId) : undefined}
                    color={eventColor(appt)}
                    onClick={onEventClick}
                  />
                ))}
                {overflow > 0 && (
                  <button
                    type="button"
                    data-testid={`month-more-${key}`}
                    className="text-xs text-slate-500 hover:text-slate-700 w-full text-left px-1"
                    onClick={(e) => {
                      e.stopPropagation()
                      onMoreClick(day)
                    }}
                  >
                    {t('schedule.calendar.more', { count: overflow })}
                  </button>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
